import {
  BlotterTable,
  BlotterTableFilters,
  Button,
  FormControlSizes,
  FormattedMessage,
  MixpanelEvent,
  WSBlotterTableMaxRecordsReachedWarning,
  createCSVFileName,
  useIntl,
  useMixpanel,
  type ExportDataAsExcelParams,
} from '@talos/kyoko';
import { useCallback } from 'react';
import { defineMessages } from 'react-intl';
import { ExportRowsModal, useExportRowsAction } from '../../ExportRowsModal/ExportRowsModal';
import { useBlotterSettings } from '../BlotterSettingsContext';
import { CustomHyperlinkButtons } from '../CustomHyperlinkButtons';
import { ShowOnlySelfFilter } from '../ShowOnlySelfFilter';
import { BlotterActions } from '../styles';
import type { TabbedBlotterPropsWithSelfFilter } from '../types';
import { useTradesBlotter } from './hooks/useTradesBlotter';

export type TradeBlotterProps = TabbedBlotterPropsWithSelfFilter;

const messages = defineMessages({
  exportTrades: {
    defaultMessage: 'Export Trades',
    id: 'Blotters.TradesBlotter.exportTrades',
  },
});

export const TradesBlotter = ({
  renderInBackground: hidden,
  loading,
  maxRows,
  onStartLoading,
  onFinishedLoading,
  suppressShowOnlySelfOrdersAndTrades = false,
}: TradeBlotterProps) => {
  const mixpanel = useMixpanel();
  const { showOnlySelfOrdersAndTrades } = useBlotterSettings();

  const { blotterTable, blotterTableFilterProps, filterBuilderAccordion } = useTradesBlotter({
    maxRows,
    showOnlySelfOrdersAndTrades,
  });

  const handleExportTrades = useCallback(
    (params: ExportDataAsExcelParams) => {
      onStartLoading();
      blotterTable.exportDataAsCSV({
        includeHiddenColumns: true,
        fileName: createCSVFileName({
          name: 'Trades',
        }),
        exportDatesRaw: params.exportDatesRaw,
      });
      onFinishedLoading();
      mixpanel.track(MixpanelEvent.ExportTrades, {
        value: params.exportDatesRaw ? 'UTC' : 'Local',
      });
    },
    [blotterTable, mixpanel, onFinishedLoading, onStartLoading]
  );
  const { modal, onClickExport } = useExportRowsAction(handleExportTrades);
  const intl = useIntl();

  return (
    <>
      {!hidden && (
        <>
          <BlotterTableFilters
            {...filterBuilderAccordion}
            {...blotterTableFilterProps}
            {...blotterTable.blotterTableFiltersProps}
            prefix={
              <BlotterActions>
                <CustomHyperlinkButtons blotter="trades" />
                <Button size={FormControlSizes.Small} loading={loading} onClick={onClickExport}>
                  <FormattedMessage {...messages.exportTrades} />
                </Button>
                {!suppressShowOnlySelfOrdersAndTrades && <ShowOnlySelfFilter type="trades" />}
              </BlotterActions>
            }
          />
        </>
      )}
      {!hidden && (
        <WSBlotterTableMaxRecordsReachedWarning {...blotterTable.paginationLimit} getTimestamp={r => r.TransactTime} />
      )}
      <BlotterTable {...blotterTable} />
      <ExportRowsModal modalProps={modal} title={intl.formatMessage(messages.exportTrades)} />
    </>
  );
};
